import { ArrowRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { cn } from "@/lib/utils"
import type { IntakeLoad, LabResults } from "@/features/intake/api/types"
import { SectionCard } from "@/features/intake/components/section-card"
import { gradeTone } from "@/features/intake/lib/intake-status"
import {
  GRADE_LIMITS,
  defectueuseSum,
} from "@/features/intake/lib/quality-rules"
import { formatNumber } from "@/lib/format"

interface QualitySummaryCardProps {
  load: IntakeLoad
}

type Tone = "clean" | "warn" | "bad" | "neutral"

const TONE_DOT: Record<Tone, string> = {
  clean: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-red-500",
  neutral: "bg-muted-foreground/40",
}

function localeNumber(n: number, decimals = 2): string {
  return formatNumber(n, decimals)
}

// Refraction-side band: clean (no refraction) / warn (refraction band) /
// bad (over the hard-reject threshold).
function refractionTone(metric: keyof LabResults, value: number): Tone {
  switch (metric) {
    case "moisture":
      if (value > 9) return "bad"
      if (value > 8) return "warn"
      return "clean"
    case "foreignMatter":
      if (value > 1.5) return "bad"
      if (value > 1) return "warn"
      return "clean"
    case "broken":
      if (value > 2.5) return "bad"
      if (value > 2) return "warn"
      return "clean"
    case "waste":
      if (value > 2) return "bad"
      if (value > 1.5) return "warn"
      return "clean"
    case "crabot":
      if (value > 3) return "bad"
      return "clean"
    default:
      return "neutral"
  }
}

// Classification-side band: G1 (clean) / G2 (warn) / sub-grade (bad).
// Only applies to the three grade drivers (mouldy / slaty / défectueuse).
function classificationToneByDriver(
  driver: "mouldy" | "slaty" | "defectueuse",
  value: number,
): Tone {
  const lim = GRADE_LIMITS[driver]
  if (value > lim.g2) return "bad"
  if (value > lim.g1) return "warn"
  return "clean"
}

interface MetricRowProps {
  label: string
  value: number
  unit: string
  tone: Tone
}

function MetricRow({ label, value, unit, tone }: MetricRowProps) {
  return (
    <div className="border-border/60 flex items-baseline justify-between gap-3 border-b py-1.5 text-sm last:border-b-0">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn("size-1.5 shrink-0 rounded-full", TONE_DOT[tone])}
        />
        <span className="text-muted-foreground">{label}</span>
      </div>
      <span className="tabular-nums font-medium">
        {localeNumber(value)}
        {unit ? <span className="text-muted-foreground ml-1">{unit}</span> : null}
      </span>
    </div>
  )
}

const REFRACTION_FIELDS: Array<keyof LabResults> = [
  "moisture",
  "foreignMatter",
  "broken",
  "waste",
  "crabot",
]

// Classification table: shows the three grade drivers (mouldy, slaty,
// défectueuse) with conformity tones, plus the raw lab inputs that feed
// défectueuse (germinated, mites, flatBeans) and the recorded-but-not-
// graded fields (grainage, violette) as neutral context.
const CLASSIFICATION_NEUTRAL_FIELDS: Array<keyof LabResults> = [
  "grainage",
  "germinated",
  "mites",
  "flatBeans",
  "violette",
]

interface FlowChipProps {
  label: string
  value: number
  tone?: "neutral" | "deduct" | "accent"
}

function FlowChip({ label, value, tone = "neutral" }: FlowChipProps) {
  return (
    <div
      className={cn(
        "flex items-baseline gap-1.5 rounded-md px-2.5 py-1.5",
        tone === "deduct"
          ? "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
          : tone === "accent"
            ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300"
            : "bg-muted text-foreground",
      )}
    >
      <span className="tabular-nums text-sm font-semibold">
        {tone === "deduct" ? "−" : ""}
        {formatNumber(value)}
      </span>
      <span className="text-[11px] opacity-70">{label}</span>
    </div>
  )
}

export function QualitySummaryCard({ load }: QualitySummaryCardProps) {
  const { t } = useTranslation("intake")
  const sampling = load.sampling
  const lab = load.labResults

  const bagsUnloaded = load.bagsUnloaded ?? 0
  const bagsWet = sampling?.bagsWet ?? 0
  const otherRejected = sampling?.otherRejected ?? 0
  const acceptedBags = sampling?.acceptedBags ?? 0

  const defectueuse = lab
    ? defectueuseSum({
        mouldy: lab.mouldy,
        slaty: lab.slaty,
        germinated: lab.germinated,
        mites: lab.mites,
        flatBeans: lab.flatBeans,
      })
    : 0

  return (
    <SectionCard
      kicker={t("quality.kicker")}
      title={t("quality.title")}
      subtitle={t("quality.summarySubtitle")}
      roleBadge={t("detail.role.lab")}
      actions={
        load.grade ? (
          <StatusBadge tone={gradeTone(load.grade)}>
            {t(`grade.${load.grade}`)}
          </StatusBadge>
        ) : null
      }
    >
      <div className="space-y-5">
        {/* Bag flow — horizontal strip */}
        <section className="space-y-2">
          <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wide">
            {t("sampling.ladderHeader")}
          </div>
          <div className="bg-muted/30 flex flex-wrap items-center gap-2 rounded-md border p-3">
            <FlowChip
              label={t("sampling.ladder.unloaded")}
              value={bagsUnloaded}
            />
            {bagsWet > 0 ? (
              <>
                <ArrowRight className="text-muted-foreground/50 size-3.5" />
                <FlowChip
                  label={t("sampling.ladder.wet")}
                  value={bagsWet}
                  tone="deduct"
                />
              </>
            ) : null}
            {otherRejected > 0 ? (
              <>
                <ArrowRight className="text-muted-foreground/50 size-3.5" />
                <FlowChip
                  label={t("sampling.ladder.otherRejected")}
                  value={otherRejected}
                  tone="deduct"
                />
              </>
            ) : null}
            <span className="text-muted-foreground/70 mx-1 text-sm">=</span>
            <FlowChip
              label={t("sampling.ladder.accepted")}
              value={acceptedBags}
              tone="accent"
            />
          </div>
        </section>

        {/* Lab analysis — two metric tables side by side */}
        <section className="grid gap-5 lg:grid-cols-2">
          <div>
            <div className="text-muted-foreground mb-2 text-[10px] font-semibold uppercase tracking-wide">
              {t("lab.refractionHeader")}
            </div>
            <div className="rounded-md border px-3 py-1">
              {REFRACTION_FIELDS.map((name) => (
                <MetricRow
                  key={name}
                  label={t(`lab.fields.${name}`)}
                  value={lab?.[name] ?? 0}
                  unit="%"
                  tone={refractionTone(name, lab?.[name] ?? 0)}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="text-muted-foreground mb-2 text-[10px] font-semibold uppercase tracking-wide">
              {t("lab.classificationHeader")}
            </div>
            <div className="rounded-md border px-3 py-1">
              <MetricRow
                label={t("lab.fields.mouldy")}
                value={lab?.mouldy ?? 0}
                unit="%"
                tone={classificationToneByDriver("mouldy", lab?.mouldy ?? 0)}
              />
              <MetricRow
                label={t("lab.fields.slaty")}
                value={lab?.slaty ?? 0}
                unit="%"
                tone={classificationToneByDriver("slaty", lab?.slaty ?? 0)}
              />
              <MetricRow
                label={t("lab.fields.defectueuse")}
                value={defectueuse}
                unit="%"
                tone={classificationToneByDriver("defectueuse", defectueuse)}
              />
              {CLASSIFICATION_NEUTRAL_FIELDS.map((name) => (
                <MetricRow
                  key={name}
                  label={t(`lab.fields.${name}`)}
                  value={lab?.[name] ?? 0}
                  unit={name === "grainage" ? "" : "%"}
                  tone="neutral"
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </SectionCard>
  )
}
